import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@platform/ui/lib/utils';

/*
 * The shape every text control shares (docs/10 §4, Input). The field ground
 * inside a boundary that holds 3:1 and darkens on hover, so a field reads as a
 * field before anyone touches it; on focus the boundary turns orange with a 2px
 * halo; an error takes the error colour. Disabled and read-only take the locked
 * ground, which each theme keeps apart from the field's own, while the value
 * stays readable. Normal is 44px, small 36px for a toolbar or a table cell and
 * 44px on a coarse pointer, as the small Button (docs/10 §3). The text is 16px
 * on a coarse pointer in both sizes: iOS Safari zooms the page when it focuses
 * a field below 16px, and the viewport must stay pinch-zoomable, so the field's
 * own size is the only lever. Read-only keys on the attribute rather than
 * `:read-only`, which every `<select>` matches.
 */
export const fieldControlVariants = cva(
    'w-full min-w-0 rounded-control border border-input bg-field text-foreground transition-[border-color,box-shadow] outline-none placeholder:text-subtle-foreground hover:border-muted-foreground focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/35 disabled:cursor-not-allowed disabled:border-border-strong disabled:bg-field-locked disabled:text-muted-foreground aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/35 [[readonly]]:bg-field-locked',
    {
        variants: {
            size: {
                md: 'h-11 px-3 text-15 pointer-coarse:text-16',
                sm: 'h-9 px-3 text-14 pointer-coarse:h-11 pointer-coarse:text-16',
            },
        },
        defaultVariants: {
            size: 'md',
        },
    },
);

export interface InputProps extends Omit<ComponentProps<'input'>, 'size'>, VariantProps<typeof fieldControlVariants> {
    /** Marks the control invalid; `Field` passes it from its error. */
    invalid?: boolean;
}

export function Input({ className, size, invalid, ...props }: InputProps) {
    return <input data-slot="input" aria-invalid={invalid || undefined} {...props} className={cn(fieldControlVariants({ size }), className)} />;
}
